// Actor Swap's actors row (live's `actors-section`, its port of the Talking Head's avatar cards,
// spec G14-templates): a card per actor with its voice, a card per actor "Add AI Actor" is making,
// then live's "+ AI Actor" tile while there is room.
import { ActionIcon, CloseButton, Loader, Menu, Tooltip, UnstyledButton } from "@mantine/core";
import { AudioLines, ImageOff, LibraryBig, Pause, Plus, Settings2, SmilePlus } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import type { ReferenceSource, Voice } from "../../../api/generated/model";
import type { PickedActor } from "../../../platform/actors/picked";
import type { Making } from "../../../platform/actors/use-makings";
import { failedWords } from "../../../platform/jobs/failed-words";
import { playsAlone, stoppedPlaying } from "../../../ui/AudioPlayer";
import classes from "./forms.module.css";

/** Live's hint on the "+ AI Actor" tile (`ACTOR_TILE_TOOLTIP`). */
const ACTOR_TIP =
  "The new actor who replaces the original speaker. Choose an existing one or generate a new " +
  "one. Note: audio can sound off when switching between genders.";

/** An actor tile's actor: one Add Actors gave, or the card's example portrait (live's upload). */
export interface SwapActorPick extends Omit<PickedActor, "source"> {
  source: ReferenceSource;
}

/** One actor of the run, one tile: its portrait and, once picked, its voice. */
export interface SwapCast {
  actor: SwapActorPick;
  /** A voice picked in Voice Settings; none keeps the actor's own (a global actor's). */
  picked: Voice | null;
}

/** Whether a tile is one of Add Actors' (live's library actors), not the example's portrait. */
export function fromLibrary(cast: SwapCast): boolean {
  return cast.actor.source.kind !== "example";
}

/** The tile's actor as Add Actors holds it: its own picks alone (live's `toSelectedActors`). */
export function libraryPick(cast: SwapCast): PickedActor[] {
  const { source } = cast.actor;
  return source.kind === "example" ? [] : [{ ...cast.actor, source }];
}

/** The voice a cast speaks with: the one picked, else the actor's own (live's recommended). */
export function voiceOf(cast: SwapCast): { id: string; name: string } | null {
  return cast.picked ?? cast.actor.voice;
}

/**
 * The casts after Add Actors' Confirm (live's `mergeLibraryActors`): the example's portraits stay
 * where they are, a kept actor keeps its voice, a new one comes after.
 */
export function recast(current: readonly SwapCast[], actors: readonly PickedActor[]): SwapCast[] {
  return [
    ...current.filter((each) => !fromLibrary(each)),
    ...actors.map(
      (actor) => current.find((each) => each.actor.key === actor.key) ?? { actor, picked: null },
    ),
  ];
}

export interface SwapCastRowProps {
  casts: readonly SwapCast[];
  /** A voice's sample, once the voices are read; none before. */
  sampleOf: (voiceId: string) => string | null;
  makings: readonly Making[];
  /** Live's four tiles: no "+ AI Actor" once they are there. */
  full: boolean;
  onRemove: (key: string) => void;
  onVoice: (key: string) => void;
  onFromLibrary: () => void;
  /** Live's "Add AI Actor"; none while no model makes actors. */
  onMakeActor: (() => void) | null;
  onDropMaking: (jobId: string) => void;
}

/** Live's actors row: the cards, the actors being made, then "+ AI Actor" and its menu. */
export function SwapCastRow(props: SwapCastRowProps) {
  const { casts, sampleOf, makings, full, onRemove, onVoice, onFromLibrary, onMakeActor } = props;
  return (
    <>
      {casts.map((cast) => {
        const voice = voiceOf(cast);
        return (
          <ActorCard
            key={cast.actor.key}
            actor={cast.actor}
            voice={voice}
            sample={voice === null ? null : (cast.picked?.preview_url ?? sampleOf(voice.id))}
            onRemove={() => onRemove(cast.actor.key)}
            onVoice={() => onVoice(cast.actor.key)}
          />
        );
      })}
      {makings.map((making) => (
        <MakingCard key={making.jobId} making={making} onRemove={() => props.onDropMaking(making.jobId)} />
      ))}
      {full ? null : (
        <Menu position="top-start">
          <Menu.Target>
            <Tooltip label={ACTOR_TIP} multiline maw={280} withArrow position="right">
              <UnstyledButton className={classes.addTile} aria-label="AI Actor">
                <Plus size={20} strokeWidth={1.5} aria-hidden />
                <span>AI Actor</span>
              </UnstyledButton>
            </Tooltip>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item leftSection={<LibraryBig size={16} strokeWidth={1.5} />} onClick={onFromLibrary}>
              From Library
            </Menu.Item>
            <Menu.Item
              leftSection={<SmilePlus size={16} strokeWidth={1.5} />}
              disabled={onMakeActor === null}
              onClick={onMakeActor ?? undefined}
            >
              Add AI Actor
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      )}
    </>
  );
}

interface ActorCardProps {
  actor: SwapActorPick;
  voice: { id: string; name: string } | null;
  sample: string | null;
  onRemove: () => void;
  onVoice: () => void;
}

/**
 * An actor's card (live's `ActorCard`): the portrait, live's x on hover, and its voice: "+ Add
 * Voice" without one, the voice's name alone while it has no sample to play, else its player and
 * the gear, both opening Voice Settings.
 */
function ActorCard({ actor, voice, sample, onRemove, onVoice }: ActorCardProps) {
  let slot;
  if (voice === null || sample === null) {
    slot = (
      <UnstyledButton className={classes.addVoice} onClick={onVoice}>
        <span className={classes.voiceIcon} aria-hidden>
          <Plus size={16} />
        </span>
        <span className={classes.voiceName}>{voice?.name ?? "Add Voice"}</span>
      </UnstyledButton>
    );
  } else {
    slot = (
      <div className={classes.voice}>
        <PlayChip url={sample} name={voice.name} owner={actor.name} />
        <ActionIcon
          variant="transparent"
          size={24}
          className={classes.gear}
          aria-label={`Voice settings: ${actor.name}`}
          onClick={onVoice}
        >
          <Settings2 size={18} />
        </ActionIcon>
      </div>
    );
  }
  return (
    <fieldset className={classes.card} aria-label={actor.name}>
      {actor.imageUrl === null ? (
        <span className={classes.portrait} aria-hidden />
      ) : (
        <img className={classes.portrait} src={actor.imageUrl} alt="" />
      )}
      {slot}
      <CloseButton size="sm" className={classes.remove} aria-label={`Remove ${actor.name}`} onClick={onRemove} />
    </fieldset>
  );
}

/** An actor "Add AI Actor" is making: a loader, or once failed live's words and its x. */
function MakingCard({ making, onRemove }: { making: Making; onRemove: () => void }) {
  const words = failedWords("image.actor", undefined);
  return (
    <fieldset className={classes.card} aria-label="New AI Actor">
      {making.failed ? (
        <div className={classes.makingFailed} role="alert">
          <ImageOff size={24} aria-hidden />
          <span className={classes.makingTitle}>{words.title}</span>
          <span className={classes.makingText}>{words.description}</span>
        </div>
      ) : (
        <div className={classes.making}>
          <Loader size={24} aria-label="Making the AI Actor" />
        </div>
      )}
      {making.failed ? (
        <CloseButton size="sm" className={classes.remove} aria-label="Remove the failed AI Actor" onClick={onRemove} />
      ) : null}
    </fieldset>
  );
}

/** Live's small player on the card (`AudioSlot`): its round button and the voice's name. */
function PlayChip({ url, name, owner }: { url: string; name: string; owner: string }) {
  const audio = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    const element = audio.current;
    return () => element?.pause();
  }, []);
  return (
    <>
      <ActionIcon
        variant="light"
        size={24}
        radius="xl"
        className={classes.play}
        data-playing={playing || undefined}
        aria-label={`${playing ? "Pause" : "Play"} ${owner}'s voice`}
        onClick={() => {
          const element = audio.current;
          if (element === null) return;
          if (element.paused) void element.play().catch(() => setPlaying(false));
          else element.pause();
        }}
      >
        {playing ? <Pause size={14} /> : <AudioLines size={16} />}
      </ActionIcon>
      <span className={classes.voiceName} title={name}>
        {name}
      </span>
      {
        // A voice sample has no captions to give.
        // oxlint-disable-next-line jsx-a11y/media-has-caption
        <audio
          ref={audio}
          src={url}
          preload="none"
          onPlay={(event) => {
            playsAlone(event.currentTarget);
            setPlaying(true);
          }}
          onPause={(event) => {
            stoppedPlaying(event.currentTarget);
            setPlaying(false);
          }}
          onEnded={(event) => {
            event.currentTarget.currentTime = 0;
          }}
        />
      }
    </>
  );
}
